import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { userApi, type Stats } from '@src/api/client';

const DashboardPage = () => {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    userApi
      .stats()
      .then(setStats)
      .catch(() => setError('Failed to load dashboard stats'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-24 rounded-xl bg-gray-200" />
        ))}
      </div>
    );
  }

  const statusCounts = stats?.statusCounts || {};

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <p className="text-gray-500">Your job search at a glance</p>
      </div>

      {error && <div className="rounded-xl bg-red-50 p-4 text-sm text-red-700">{error}</div>}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard title="Total Applications" value={stats?.totalApplications || 0} color="brand" />
        <StatCard
          title="Today's Applications"
          value={`${stats?.applicationsToday || 0} / ${stats?.dailyLimit || 5}`}
          color="blue"
        />
        <StatCard title="Interviews" value={statusCounts['interview'] || 0} color="green" />
        <StatCard title="Offers" value={statusCounts['offer'] || 0} color="yellow" />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl bg-white p-6 shadow-sm">
          <h2 className="mb-4 text-lg font-semibold">Status Breakdown</h2>
          <div className="space-y-3">
            {Object.entries(statusCounts).map(([status, count]) => (
              <div key={status} className="flex items-center justify-between">
                <span className="capitalize text-gray-600">{status}</span>
                <span className="font-medium">{count}</span>
              </div>
            ))}
            {Object.keys(statusCounts).length === 0 && <p className="text-sm text-gray-400">No applications yet</p>}
          </div>
        </div>

        <div className="rounded-xl bg-white p-6 shadow-sm">
          <h2 className="mb-4 text-lg font-semibold">Quick Actions</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <Link
              to="/tailor"
              className="border-brand-200 bg-brand-50 hover:bg-brand-100 rounded-lg border p-4 text-center transition-colors">
              <p className="text-brand-700 font-medium">Tailor Resume</p>
              <p className="text-brand-500 mt-1 text-xs">AI-optimize for a job</p>
            </Link>
            <Link
              to="/resumes"
              className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-center transition-colors hover:bg-gray-100">
              <p className="font-medium text-gray-700">Manage Resumes</p>
              <p className="mt-1 text-xs text-gray-500">Upload and edit</p>
            </Link>
            <Link
              to="/jobs"
              className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-center transition-colors hover:bg-gray-100">
              <p className="font-medium text-gray-700">Saved Jobs</p>
              <p className="mt-1 text-xs text-gray-500">View saved positions</p>
            </Link>
            <Link
              to="/applications"
              className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-center transition-colors hover:bg-gray-100">
              <p className="font-medium text-gray-700">Applications</p>
              <p className="mt-1 text-xs text-gray-500">Track your progress</p>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ title, value, color }: { title: string; value: number | string; color: string }) => {
  const colors: Record<string, string> = {
    brand: 'bg-brand-50 text-brand-700',
    blue: 'bg-blue-50 text-blue-700',
    green: 'bg-green-50 text-green-700',
    yellow: 'bg-yellow-50 text-yellow-700',
  };

  return (
    <div className="rounded-xl bg-white p-5 shadow-sm">
      <p className="text-sm text-gray-500">{title}</p>
      <p className={`mt-2 text-2xl font-bold ${colors[color]?.split(' ')[1] || 'text-gray-900'}`}>{value}</p>
    </div>
  );
};

export default DashboardPage;
